import React, { forwardRef, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { LuMousePointerClick } from 'react-icons/lu';
import { useTooltip, TooltipPosition, TooltipTrigger } from '@/context/Ui';

export interface RoundButtonProps {
  className?: string;
  role?: string;
  ariaLabel?: string;
  dataCy?: string;
  kind?: 'button' | 'submit';
  onClick?: () => void;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  color?: 'blue' | 'red' | 'green' | 'white';
  disabled?: boolean;
  pressHint?: boolean;
  tooltipText?: string;
  tooltipPosition?: TooltipPosition;
  tooltipTrigger?: TooltipTrigger;
}

export interface RoundButtonHandle {
  showTooltip: () => void;
  hideTooltip: () => void;
}

export const RoundButton = forwardRef<
  RoundButtonHandle,
  React.PropsWithChildren<RoundButtonProps>
>(
  (
    {
      children,
      className,
      role,
      ariaLabel,
      dataCy,
      kind = 'button',
      onClick,
      size = 'md',
      color = 'blue',
      disabled = false,
      pressHint = false,
      tooltipText,
      tooltipPosition = 'top',
      tooltipTrigger = 'hover',
    },
    ref,
  ) => {
    const { tooltipElementRef, showTooltip, hideTooltip } = useTooltip(
      tooltipText,
      {
        position: tooltipPosition,
        trigger: tooltipTrigger,
      },
    );

    useImperativeHandle(ref, () => ({
      showTooltip: () => showTooltip(),
      hideTooltip: () => hideTooltip(),
    }));

    return (
      <button
        ref={
          tooltipText
            ? (tooltipElementRef as React.RefObject<HTMLButtonElement>)
            : undefined
        }
        className={clsx(
          'flex items-center justify-center',
          'rounded-full text-center',
          'disabled:opacity-50',
          size === 'xs' && 'w-12 h-12 lg:w-16 lg:h-16 p-1',
          size === 'sm' && 'w-20 h-20 lg:w-24 lg:h-24 p-2',
          size === 'md' && 'w-30 h-30 lg:w-36 lg:h-36 p-4',
          size === 'lg' && 'w-4Hint0 h-40 lg:w-48 lg:h-48 p-6',
          color === 'blue' && 'text-white bg-blue-normal hover:bg-blue-dark',
          color === 'red' && 'text-white bg-red-normal hover:bg-red-dark',
          color === 'green' && 'text-white bg-green-dark hover:bg-green-darker',
          color === 'white' &&
            'text-pt-ds-ink bg-white shadow-[0_8px_0_rgba(0,0,0,0.15)] transition-transform hover:-translate-y-0.5 active:translate-y-1',
          !className?.includes('absolute') && 'relative',
          className,
        )}
        data-cy={dataCy}
        role={role}
        aria-label={ariaLabel}
        type={kind}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
        {pressHint && (
          <LuMousePointerClick
            className={clsx(
              color === 'blue' && 'text-blue-normal',
              color === 'red' && 'text-red-normal',
              color === 'green' && 'text-green-normal',
              color === 'white' && 'text-pt-ds-ink',
              'absolute left-full top-full -translate-y-full -translate-x-full w-8 h-8 animate-ping',
            )}
          />
        )}
      </button>
    );
  },
);

RoundButton.displayName = 'RoundButton';
